import React from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Typography, Paper, IconButton, Avatar } from '@mui/material';
import { KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, isSameDay, startOfDay, startOfWeek, endOfWeek } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import useSWR from 'swr';

import useQueryInlineEdit from 'src/pages/QueryPages/hooks/useQueryInlineEdit';
import EditableCell from 'src/pages/QueryPages/components/EditableCell';
import EditFieldModal from 'src/pages/QueryPages/components/EditFieldModal';
import { QUERY_TABLE_VISIBILITY } from 'src/constants/config';
import { queryApi } from 'src/api/queryApi';
import { expertApi } from 'src/api/expertApi';
import { setTablePageSize } from 'src/store/slices/viewModeSlice';
import QueryStatus from 'src/pages/QueryPages/components/QueryStatus';
import UserSelect from 'src/pages/QueryPages/components/UserSelect';
import CompanySelect from 'src/pages/QueryPages/components/CompanySelect';
import ClaimButton from 'src/components/ClaimButton';
import { trimNameToThreeWords } from 'src/utils/string';
import { parseQueryNotes } from 'src/utils/queryNotesUtils';

const QueryTimeline = ({ queries, users, path, mutate, currentSearchParams, pageType }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tablePageSize = useSelector((state) => state.viewMode.tablePageSize) || 25;

  const [groupPages, setGroupPages] = React.useState({});

  const { editModal, handleEditValueChange, handleOpenEditModal, handleCloseEditModal, handleSaveFromModal, handleSaveEdit } = useQueryInlineEdit(
    queries,
    mutate
  );

  const expertsFetcher = () => expertApi.getExperts();
  const { data: experts } = useSWR('experts', expertsFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000,
  });

  const expertIdToExpert = React.useMemo(() => {
    const map = new Map();
    (experts || []).forEach((expert) => {
      map.set(Number(expert.id), expert);
    });
    return map;
  }, [experts]);

  const getExpertInfo = React.useCallback(
    (row) => {
      if (row.status === 'draft') {
        const parsedNotes = parseQueryNotes(row.notes);
        if (parsedNotes.draftCompany) {
          return { name: '—', company: parsedNotes.draftCompany, avatar: null };
        }
      }

      if (!row || row.status === 'new' || row.status === 'draft') {
        return { name: '—', company: '—', avatar: null };
      }

      const preferredId = Number(row.expertsID);
      const primaryId = Number(row.expertID);
      const matchedExpert = expertIdToExpert.get(preferredId) || expertIdToExpert.get(primaryId);

      if (!matchedExpert) {
        return { name: 'Unknown Client', company: 'Unknown Company' };
      }

      const name = trimNameToThreeWords(matchedExpert.name);
      const company = matchedExpert.company || 'Unknown Company';
      const avatar = matchedExpert.imageURL || null;
      return { name, company, avatar };
    },
    [expertIdToExpert]
  );

  const userIdToUser = React.useMemo(() => {
    const map = new Map();
    (users || []).forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [users]);

  const formatDateHeader = React.useCallback((dateStr, isWeekGroup = false) => {
    const date = new Date(dateStr.split('T')[0] + 'T00:00:00');

    if (isWeekGroup) {
      const weekStart = startOfWeek(date, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
      return `Week of ${format(weekStart, 'MMM dd')} - ${format(weekEnd, 'MMM dd, yyyy')}`;
    }

    const today = startOfDay(new Date());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, tomorrow)) return 'Tomorrow';
    return format(date, 'MMM dd, yyyy');
  }, []);

  const groupedQueries = React.useMemo(() => {
    const groups = {};
    const isNewQueries = pageType === 'new';

    if (isNewQueries) {
      (queries || []).forEach((query) => {
        if (!query.dueDate) {
          const dateKey = 'no-deadline';
          if (!groups[dateKey]) groups[dateKey] = [];
          groups[dateKey].push(query);
        } else {
          const deadline = new Date(query.dueDate);
          const weekStart = startOfWeek(deadline, { weekStartsOn: 1 });
          const dateKey = format(weekStart, 'yyyy-MM-dd');
          if (!groups[dateKey]) groups[dateKey] = [];
          groups[dateKey].push(query);
        }
      });
    } else {
      (queries || []).forEach((query) => {
        let dateWriting = null;
        if (query.notes) {
          try {
            const parsed = JSON.parse(query.notes);
            if (typeof parsed === 'object' && parsed !== null) {
              dateWriting = parsed.dateWriting;
            }
          } catch (e) {
            // Malformed notes JSON
          }
        }

        const effectiveDate = dateWriting || new Date().toISOString();
        const dateKey = effectiveDate.split('T')[0];
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(query);
      });
    }

    Object.keys(groups).forEach((dateKey) => {
      groups[dateKey].sort((a, b) => {
        const aTime = parseQueryNotes(a.notes).dateWriting;
        const bTime = parseQueryNotes(b.notes).dateWriting;
        if (!aTime) return 1;
        if (!bTime) return -1;
        return new Date(aTime) - new Date(bTime);
      });
    });

    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'no-deadline') return 1;
      if (b === 'no-deadline') return -1;
      return new Date(a) - new Date(b);
    });

    return sortedKeys.map((dateKey) => ({
      dateKey,
      dateLabel: dateKey === 'no-deadline' ? 'No Deadline' : formatDateHeader(dateKey, isNewQueries),
      queries: groups[dateKey],
    }));
  }, [queries, formatDateHeader, pageType]);

  const handlePageSizeChange = (newPageSize) => {
    dispatch(setTablePageSize(newPageSize));
  };

  const handleDiscardQuery = React.useCallback(
    async (queryID) => {
      const response = await queryApi.updateQuery(queryID, 'discarded');
      if (response.status === 'Query Updated') {
        toast.success('Query discarded successfully');
        mutate();
      } else {
        toast.error('Failed to discard query');
      }
    },
    [mutate]
  );

  const handleMoveUp = React.useCallback(
    async (queryId) => {
      const group = groupedQueries.find((g) => g.queries.some((q) => q.id === queryId));
      if (!group) return;

      const sortedQueries = group.queries;
      const currentIndex = sortedQueries.findIndex((q) => q.id === queryId);
      if (currentIndex <= 0) return;

      const currentQuery = sortedQueries[currentIndex];
      const aboveQuery = sortedQueries[currentIndex - 1];
      const aboveAboveQuery = sortedQueries[currentIndex - 2];

      const aboveTime = new Date(parseQueryNotes(aboveQuery.notes).dateWriting || new Date()).getTime();
      const aboveAboveTime = aboveAboveQuery
        ? new Date(parseQueryNotes(aboveAboveQuery.notes).dateWriting || new Date()).getTime()
        : aboveTime - 3600000;

      const newTime = new Date(Math.floor((aboveAboveTime + aboveTime) / 2));

      const currentDateWriting = parseQueryNotes(currentQuery.notes).dateWriting;
      const currentDate = currentDateWriting ? new Date(currentDateWriting) : new Date();
      newTime.setFullYear(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

      try {
        await handleSaveEdit(queryId, 'dateWriting', newTime);
      } catch (error) {
        // Handled in handleSaveEdit via toast
      }
    },
    [groupedQueries, handleSaveEdit]
  );

  const handleMoveDown = React.useCallback(
    async (queryId) => {
      const group = groupedQueries.find((g) => g.queries.some((q) => q.id === queryId));
      if (!group) return;

      const sortedQueries = group.queries;
      const currentIndex = sortedQueries.findIndex((q) => q.id === queryId);
      if (currentIndex < 0 || currentIndex >= sortedQueries.length - 1) return;

      const currentQuery = sortedQueries[currentIndex];
      const belowQuery = sortedQueries[currentIndex + 1];
      const belowBelowQuery = sortedQueries[currentIndex + 2];

      const belowTime = new Date(parseQueryNotes(belowQuery.notes).dateWriting || new Date()).getTime();
      const belowBelowTime = belowBelowQuery
        ? new Date(parseQueryNotes(belowBelowQuery.notes).dateWriting || new Date()).getTime()
        : belowTime + 3600000;

      const newTime = new Date(Math.floor((belowTime + belowBelowTime) / 2));

      const currentDateWriting = parseQueryNotes(currentQuery.notes).dateWriting;
      const currentDate = currentDateWriting ? new Date(currentDateWriting) : new Date();
      newTime.setFullYear(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

      try {
        await handleSaveEdit(queryId, 'dateWriting', newTime);
      } catch (error) {
        // Handled in handleSaveEdit via toast
      }
    },
    [groupedQueries, handleSaveEdit]
  );

  const createQueryPath = React.useCallback(
    (queryId) => {
      const basePath = path.replace(':queryID', queryId);
      return currentSearchParams ? `${basePath}?${currentSearchParams}` : basePath;
    },
    [path, currentSearchParams]
  );

  const getVisibilityModel = React.useMemo(() => {
    const model = { ...QUERY_TABLE_VISIBILITY.DEFAULT };

    if (pageType === 'new') {
      model.sortOrder = false;
    }

    const presentStatuses = new Set((queries || []).map((q) => q.status));
    const allRuleColumns = new Set(Object.values(QUERY_TABLE_VISIBILITY.STATUS_RULES).flatMap((rules) => Object.keys(rules)));

    allRuleColumns.forEach((columnKey) => {
      let hideForAllStatuses = true;
      for (const status of presentStatuses) {
        const rules = QUERY_TABLE_VISIBILITY.STATUS_RULES[status];
        if (!rules || rules[columnKey] !== false) {
          hideForAllStatuses = false;
          break;
        }
      }
      if (hideForAllStatuses) {
        model[columnKey] = false;
      }
    });

    return model;
  }, [queries, pageType]);

  const getRowsForGroup = React.useCallback(
    (groupQueries) => {
      return groupQueries.map((row) => {
        const info = getExpertInfo(row);
        const parsedNotes = parseQueryNotes(row.notes);

        return {
          ...row,
          expertName: info.name,
          expertCompany: info.company,
          expertAvatar: info.avatar,
          dateWriting: parsedNotes.dateWriting,
          parsedNotes: parsedNotes.notes,
          draftCompany: parsedNotes.draftCompany,
        };
      });
    },
    [getExpertInfo]
  );

  const formatDateToInput = (date) => {
    if (!date) return '';
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const columns = React.useMemo(
    () => [
      {
        field: 'sortOrder',
        headerName: '',
        minWidth: 50,
        flex: 0.3,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMoveUp(params.row.id);
              }}
              sx={{ p: 0, '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMoveDown(params.row.id);
              }}
              sx={{ p: 0, '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      {
        field: 'dateWriting',
        headerName: 'Date writing',
        minWidth: 100,
        flex: 0.7,
        renderCell: (params) => {
          const currentDate = params.row.dateWriting ? new Date(params.row.dateWriting) : null;
          return (
            <EditableCell
              value={currentDate ? format(currentDate, 'MMM dd') : ''}
              placeholder="No date"
              onClick={() => handleOpenEditModal(params.row.id, 'dateWriting', formatDateToInput(currentDate), params.row.dateWriting)}
            />
          );
        },
      },
      {
        field: 'dueDate',
        headerName: 'Deadline',
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
          const currentDate = params.row.dueDate ? new Date(params.row.dueDate) : null;
          return (
            <EditableCell
              value={currentDate ? format(currentDate, 'MMM dd') : ''}
              placeholder="No date"
              onClick={() => handleOpenEditModal(params.row.id, 'deadlineDate', currentDate ? format(currentDate, 'yyyy-MM-dd') : '')}
            />
          );
        },
      },
      {
        field: 'dueTime',
        headerName: 'Time',
        minWidth: 100,
        flex: 0.7,
        sortable: false,
        renderCell: (params) => {
          const currentDate = params.row.dueDate ? new Date(params.row.dueDate) : null;
          return (
            <EditableCell
              value={currentDate ? format(currentDate, 'HH:mm') : ''}
              placeholder="No time"
              onClick={() => handleOpenEditModal(params.row.id, 'deadlineTime', currentDate ? format(currentDate, 'HH:mm') : '')}
            />
          );
        },
      },
      {
        field: 'company',
        headerName: 'Company',
        minWidth: 180,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => {
          if (params.row.status === 'draft') {
            return (
              <Box sx={{ width: '100%' }}>
                <CompanySelect query={params.row} mutate={mutate} />
              </Box>
            );
          }
          return <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{params.row.expertCompany}</Box>;
        },
      },
      {
        field: 'expert',
        headerName: 'Expert',
        minWidth: 200,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Avatar src={params.row.expertAvatar} sx={{ width: 24, height: 24, flexShrink: 0, '& img': { objectFit: 'cover' } }}>
              {params.row.expertName?.charAt(0)}
            </Avatar>
            {params.row.expertName}
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 180,
        flex: 1.2,
        renderCell: (params) => <QueryStatus query={params.row} mutate={mutate} />,
      },
      {
        field: 'title',
        headerName: 'Title',
        minWidth: 500,
        flex: 4.3,
        renderCell: (params) => (
          <div
            style={{
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
            onClick={() => navigate(createQueryPath(params.row.id))}
          >
            {params.value}
          </div>
        ),
      },
      {
        field: 'claimedBy',
        headerName: 'Claimed by',
        minWidth: 180,
        flex: 1.2,
        renderCell: (params) => (
          <Box sx={{ width: '100%' }}>
            <UserSelect users={users} selectedUserId={params.row.userClaimID} query={params.row} mutate={mutate} />
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 220,
        flex: 1.3,
        sortable: false,
        renderCell: (params) => {
          const claimedUser = userIdToUser.get(params.row.userClaimID);
          return (
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <ClaimButton
                query={params.row}
                claimedUser={claimedUser}
                mutate={mutate}
                sx={{
                  display: 'flex',
                  minWidth: 'auto',
                  width: '100%',
                  '& .MuiButton-root': { padding: '4px 8px', minWidth: 0, whiteSpace: 'nowrap' },
                }}
                handleDiscardQuery={() => handleDiscardQuery(params.row.id)}
                hideAvatar
              />
            </Box>
          );
        },
      },
      {
        field: 'notes',
        headerName: 'Note',
        minWidth: 300,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <EditableCell
            value={params.row.parsedNotes || ''}
            placeholder="Click to add note..."
            onClick={() => handleOpenEditModal(params.row.id, 'notes', params.row.parsedNotes || '')}
          />
        ),
      },
    ],
    [userIdToUser, mutate, navigate, handleDiscardQuery, createQueryPath, handleOpenEditModal, handleMoveUp, handleMoveDown, users]
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {groupedQueries.map((group) => {
          const rows = getRowsForGroup(group.queries);
          const currentPage = groupPages[group.dateKey] || 0;

          return (
            <Paper key={group.dateKey} sx={{ overflow: 'hidden', boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderBottom: 2,
                  borderColor: 'primary.dark',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {group.dateLabel}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  {group.queries.length} {group.queries.length === 1 ? 'query' : 'queries'}
                </Typography>
              </Box>

              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ pageSize: tablePageSize, page: currentPage }}
                  onPaginationModelChange={(model) => {
                    setGroupPages((prev) => ({ ...prev, [group.dateKey]: model.page }));
                    handlePageSizeChange(model.pageSize);
                  }}
                  disableSelectionOnClick
                  autoHeight
                  getRowId={(row) => row.id}
                  isRowSelectable={() => false}
                  columnVisibilityModel={getVisibilityModel}
                  hideFooter={rows.length < 20}
                  sx={{
                    borderColor: 'divider',
                    backgroundColor: 'white',
                    '& .MuiDataGrid-cell': {
                      borderRight: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                      px: 2,
                      overflow: 'visible !important',
                      whiteSpace: 'nowrap',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      borderBottom: 2,
                      borderColor: 'divider',
                      backgroundColor: 'white',
                      '& .MuiDataGrid-columnHeader': {
                        py: 2,
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
                      },
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: 1,
                      borderColor: 'divider',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                />
              </Box>
            </Paper>
          );
        })}
      </Box>

      <EditFieldModal
        editModal={editModal}
        onValueChange={handleEditValueChange}
        onClose={handleCloseEditModal}
        onSave={handleSaveFromModal}
        dateWritingInputType="date"
      />
    </>
  );
};

QueryTimeline.propTypes = {
  queries: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  path: PropTypes.string.isRequired,
  mutate: PropTypes.func.isRequired,
  currentSearchParams: PropTypes.string,
  pageType: PropTypes.string,
};

export default QueryTimeline;
